import React, { useState, useEffect } from 'react';
import { useChatState } from '../../contexts/ChatStateContext';

/**
 * Debug panel to show what ChatSidebar thinks it should be displaying
 * based on the current ChatStateContext
 */
const ChatStateDebugPanel = ({ darkMode = false }) => {
  const { backendChatState, pendingActions } = useChatState();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stateHistory, setStateHistory] = useState([]);

  // Track state changes and log them with timestamps
  useEffect(() => {
    const timestamp = new Date().toLocaleTimeString();
    setLastUpdate(timestamp);
    
    // Analyze what should be shown based on current state
    const analysis = analyzeCurrentState(backendChatState, pendingActions);
    
    // Add to history (keep last 5 entries)
    setStateHistory(prev => [
      { timestamp, analysis, backendChatState, pendingActions },
      ...prev.slice(0, 4)
    ]);
    
    console.log('🔍 [ChatStateDebug] State changed at', timestamp, analysis);
  }, [backendChatState, pendingActions]);

  const analyzeCurrentState = (chatState, actions) => {
    if (!chatState) {
      return {
        shouldShow: 'No backend chat state - using ChatContext fallback',
        messageSource: 'context',
        chatMode: 'unknown',
        welcomeType: 'fallback',
        actionCount: actions.length,
        actions: actions.map(a => a.type)
      };
    }

    const analysis = {
      shouldShow: '',
      messageSource: '',
      chatMode: '',
      welcomeType: '',
      actionCount: actions.length,
      actions: actions.map(a => a.type),
      rawState: {
        mode: chatState.mode,
        isOnboarding: chatState.is_onboarding,
        errorState: chatState.error_state,
        hasMessages: !!chatState.messages,
        messageCount: chatState.messages?.length || 0,
        matchedProperty: chatState.matched_property,
        propertyMatchFound: chatState.property_match_found
      }
    };

    // 1. Determine message source
    if (chatState.messages && chatState.messages.length > 0) {
      analysis.messageSource = 'backend_messages';
      analysis.shouldShow = `Show ${chatState.messages.length} backend messages`;
    } else {
      analysis.messageSource = 'generate_welcome';
      analysis.shouldShow = 'Generate welcome message';
    }

    // 2. Determine chat mode and welcome type
    if (chatState.is_onboarding) {
      if (chatState.mode === 'onboarding') {
        analysis.chatMode = 'onboarding_flow';
        analysis.welcomeType = 'onboarding_welcome';
        analysis.shouldShow += ' + Onboarding flow';
      } else if (chatState.mode === 'property_selector') {
        analysis.chatMode = 'property_selector';
        analysis.welcomeType = 'matrix_property_selector';
        analysis.shouldShow += ' + Property selector modal';
      } else {
        analysis.chatMode = 'onboarding_unknown';
        analysis.welcomeType = 'generic_onboarding';
        analysis.shouldShow += ' + Generic onboarding';
      }
    } else {
      if (chatState.mode === 'normal') {
        analysis.chatMode = 'normal_conversation';
        analysis.welcomeType = analysis.messageSource === 'generate_welcome' ? 'property_welcome' : 'existing_conversation';
        analysis.shouldShow += ' + Normal chat';
      } else if (chatState.mode === 'no_property') {
        analysis.chatMode = 'no_property';
        analysis.welcomeType = 'connect_property';
        analysis.shouldShow += ' + Connect property prompt';
      } else {
        analysis.chatMode = 'unknown_mode';
        analysis.welcomeType = 'fallback_welcome';
        analysis.shouldShow += ' + Fallback mode';
      }
    }

    // 3. Add error state info
    if (chatState.error_state) {
      analysis.shouldShow += ` + ERROR: ${chatState.error_state}`;
    }

    return analysis;
  };

  if (isCollapsed) {
    return (
      <div 
        className={`fixed top-4 right-4 z-50 ${
          darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
        } border rounded-lg p-2 cursor-pointer hover:shadow-lg transition-shadow`}
        onClick={() => setIsCollapsed(false)}
      >
        <div className={`text-xs font-mono ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
          💬 Debug {lastUpdate && `(${lastUpdate})`}
        </div>
      </div>
    );
  }

  const currentAnalysis = stateHistory[0]?.analysis;

  return (
    <div className={`fixed top-4 right-4 z-50 ${
      darkMode ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
    } border rounded-lg p-4 max-w-md max-h-96 overflow-auto shadow-xl`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h3 className={`font-bold text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
          💬 Chat State Debug
        </h3>
        <button
          onClick={() => setIsCollapsed(true)}
          className={`text-xs px-2 py-1 rounded ${
            darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
          }`}
        >
          ✕
        </button>
      </div>

      {/* Current State */}
      {currentAnalysis && (
        <div className="mb-4">
          <div className={`text-xs font-mono mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            Last Update: {stateHistory[0]?.timestamp}
          </div>
          
          <div className="space-y-2 text-xs">
            <div>
              <strong>Should Show:</strong>
              <div className={`font-mono p-1 rounded text-xs ${
                darkMode ? 'bg-gray-700 text-yellow-300' : 'bg-yellow-50 text-yellow-800'
              }`}>
                {currentAnalysis.shouldShow}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <strong>Chat Mode:</strong>
                <div className={`font-mono text-xs ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                  {currentAnalysis.chatMode}
                </div>
              </div>
              <div>
                <strong>Welcome:</strong>
                <div className={`font-mono text-xs ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                  {currentAnalysis.welcomeType}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <strong>Messages:</strong>
                <div className={`font-mono text-xs ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  {currentAnalysis.messageSource}
                </div>
              </div>
              <div>
                <strong>Actions:</strong>
                <div className={`font-mono text-xs ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                  {currentAnalysis.actionCount} pending
                </div>
              </div>
            </div>

            {/* Raw State Summary */}
            <div className="border-t pt-2 mt-2">
              <strong>Raw State:</strong>
              <div className={`font-mono text-xs space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <div>Mode: {currentAnalysis.rawState.mode}</div>
                <div>Onboarding: {currentAnalysis.rawState.isOnboarding ? 'Yes' : 'No'}</div>
                <div>Messages: {currentAnalysis.rawState.messageCount}</div>
                {currentAnalysis.rawState.errorState && (
                  <div className={`${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    Error: {currentAnalysis.rawState.errorState}
                  </div>
                )}
              </div>
            </div>

            {/* Pending Actions */}
            {currentAnalysis.actions.length > 0 && (
              <div className="border-t pt-2 mt-2">
                <strong>Pending Actions:</strong>
                <div className={`font-mono text-xs space-y-1 ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                  {currentAnalysis.actions.map((action, i) => (
                    <div key={i}>• {action}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* State History */}
      {stateHistory.length > 1 && (
        <div className="border-t pt-3 mt-3">
          <div className={`text-xs font-bold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Recent Changes:
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {stateHistory.slice(1).map((entry, i) => (
              <div key={i} className={`text-xs p-2 rounded ${
                darkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`font-mono text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {entry.timestamp}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {entry.analysis.shouldShow}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatStateDebugPanel;

